import { Card, Flex, Heading, Tag, TagProps } from '@applyboard/crystal-ui'

import { ApplicationDetailsFile } from './types'
import { ApplicationDetailCardActions } from './ApplicationDetailCardActions'
import { ApplicationDetailCardDescription } from './ApplicationDetailCardDescription'
import { ApplicationDetailFile } from '../ApplicationDetailFile'
import { ApplicationDetailCardHelper } from './ApplicationDetailCardHelper'
import { ApplicationDetailCardRejectDialogProps } from './ApplicationDetailCardActions/ApplicationDetailCardRejectDialog'
import { ApplicationDetailCardAcceptDialogProps } from './ApplicationDetailCardActions/ApplicationDetailCardAcceptDialog'
import { DecisionResponseType, DocumentType } from '@applyboard/school-applications-types-lib'

type ApplicationDetailCardProps = Readonly<{
  title: string
  issuedOn: Date
  file: ApplicationDetailsFile | undefined
  status?: DecisionResponseType | string
  statusChangedOn?: Date
  rejectDialog?: ApplicationDetailCardRejectDialogProps | null
  onOfferConditionsClick?: () => void
  offerType?: 'Conditional' | 'Unconditional'
  activeRecord?: boolean
  acceptDialog?: ApplicationDetailCardAcceptDialogProps | null
  loading?: boolean
  documentType?: DocumentType
  revokedAt?: Date
  replacedAt?: Date
}>

export function ApplicationDetailCard(props: ApplicationDetailCardProps) {
  const isRevoked = Boolean(props.revokedAt)
  let tagData:
    | {
        label: TagProps['children']
        intent: TagProps['intent']
      }
    | undefined

  switch (true) {
    case isRevoked:
      tagData = {
        label: 'Revoked',
        intent: 'negative',
      }
      break
    case !props.activeRecord:
      tagData = {
        label: 'Replaced',
        intent: 'magic',
      }
      break
    case props.documentType === DocumentType.DECLINED_LETTER:
      tagData = {
        label: 'Declined',
        intent: 'negative',
      }
      break
    case props.status === DecisionResponseType.REJECTED:
      tagData = {
        label: 'Rejected',
        intent: 'negative',
      }
      break
    case props.status === DecisionResponseType.ACCEPTED:
      tagData = {
        label: 'Accepted',
        intent: 'positive',
      }
      break
    case props.status === DecisionResponseType.PENDING &&
      props.documentType === DocumentType.PRE_OFFER_LETTER:
      tagData = {
        label: 'Pending review',
        intent: 'warning',
      }
      break
    default:
      tagData = undefined
      break
  }

  return (
    <Card aria-label="decision letter">
      <Flex direction="column">
        <Flex direction="column" gap={4}>
          <Flex
            gap={3}
            align="center"
            justify={{ xs: 'between', lg: 'start' }}
            grow={1}
            wrap
          >
            <Flex gap={3} align="center">
              <Heading variant="titleS" level={3}>
                {props.title}
              </Heading>
            </Flex>
            {tagData ? <Tag intent={tagData.intent}>{tagData.label}</Tag> : null}
          </Flex>

          <Flex>
            <ApplicationDetailCardHelper
              activeRecord={Boolean(props.activeRecord)}
              decisionDocumentTag={props.documentType as DocumentType}
              status={props.status as DecisionResponseType | undefined}
              isRevoked={isRevoked}
            />
          </Flex>

          <Flex
            gap={{ lg: 4 }}
            direction={{ xs: 'column', lg: 'row' }}
            align={{ lg: 'center' }}
            justify={{ xs: 'between', lg: 'between' }}
            pb={{ lg: props.status ? 0 : 4 }}
          >
            <Flex.Item basis={{ lg: '53%' }}>
              <ApplicationDetailCardDescription
                status={props.status}
                issuedOn={props.issuedOn}
                revokedAt={props.revokedAt}
                statusChangedOn={props.statusChangedOn}
                activeRecord={props.activeRecord}
                replacedAt={props.replacedAt}
                offerType={props.offerType}
              />
            </Flex.Item>
          </Flex>
          <Flex
            direction={{ xs: 'column', lg: 'row' }}
            justify="between"
            align="baseline"
            gap={4}
            pt={4}
          >
            {!isRevoked && props.file ? (
              <Flex.Item basis={{ xs: '100%', lg: '43%' }}>
                <ApplicationDetailFile url={props.file.url} name={props.file.name} />
              </Flex.Item>
            ) : null}
            {(props.acceptDialog && props.rejectDialog) || props.onOfferConditionsClick ? (
              <Flex.Item>
                <Flex
                  direction={{ xs: 'column', lg: 'row' }}
                  justify="end"
                  gap={{ xs: 4, lg: 2 }}
                  pb={{ xs: 4, lg: 0 }}
                >
                  <ApplicationDetailCardActions
                    rejectDialog={props.rejectDialog}
                    status={props.status}
                    onOfferConditionsClick={props.onOfferConditionsClick}
                    loading={props.loading}
                    acceptDialog={props.acceptDialog}
                    documentType={props.documentType}
                  />
                </Flex>
              </Flex.Item>
            ) : null}
          </Flex>
        </Flex>
      </Flex>
    </Card>
  )
}
